import { ICONS } from "@/common/icons.map";
import {
  AskForEventJustification,
  InvestigateEvent,
  ManageTrustMenuItem,
} from "@/common/menu-items";
import { POPULATION_TYPE } from "@/common/models";
import { SharedState } from "@/common/state";
import {
  DrillDownMenuItem,
  UseDrillDownMenu,
} from "@/components/root/drilldown-menu/drilldown-menu.state";
import { UseModal } from "@/components/root/modal";
import { isEmpty, uniq } from "@/fp";
import { getEmailAddressFromExtendedData } from "@/gql/types";
import ManageTrustModalVue from "@/views/customize/in-motion/exposure-control/manage-trust-modal/ManageTrustModal.vue";

export const ms_event_menu = async (p: {
  item: any;
  event: MouseEvent;
  /**
   * Suppress "Ask For Justification". A justification is a request to a person
   * to explain their own action; on the Gen AI dashboard the actor is an AI
   * agent, so there is nobody to ask. Every other source is unaffected.
   */
  hide_justification?: boolean;
}): Promise<"close" | "idle"> => {
  let eventEntities = [];

  const { items, exposedTo, exposedBy } = p.item;

  eventEntities = [...exposedTo, exposedBy].filter((f) => f);
  const eventData = {
    senderName: p.item.exposedBy?.email || p.item.exposedBy?.name,
    files: items,
    exposedTo: exposedTo.map((d: any) => {
      return {
        ...d,
        externalId: getEmailAddressFromExtendedData(d),
      };
    }),
  };

  const employees = eventEntities
    .filter((f) => !f.isExternal || (f.isExternal && f.isPersonalAddress))
    .map((l) => {
      return {
        ...l,
        icon: ICONS.ENTITIES.get(POPULATION_TYPE.EMPLOYEE),
      };
    });
  const organizations = eventEntities
    .filter((f) => f.type == "organization")
    .map((l) => {
      return {
        ...l,
        icon: ICONS.ENTITIES.get(POPULATION_TYPE.ORGANIZATION),
      };
    });
  const [
    justification_action_name,
    justification_menu_item,
    justification_handler,
  ] = AskForEventJustification;

  const menu: DrillDownMenuItem[] = [InvestigateEvent(eventData)];

  const does_user_never_asked_for_justification = isEmpty(
    p.item.justification?.id,
  );

  const resolve_section_sub_items = [];

  const l0_labels_from_event = items
    .filter((item: any) => item?.labels?.infoLabel?.id)
    .map(({ labels: { infoLabel } }: any) => {
      const label = SharedState.allInfoTypes.value[infoLabel.id];
      if (!label) return null;
      if (label.generation == 0) {
        return label.id;
      } else {
        const l0 = SharedState.get_l0_by_code(label.code);
        return l0 ? l0.id : null;
      }
    })
    .filter(Boolean)
    .flat();
  /* 
    if the event is dealing with only one L0 and this L0 is "other"
    than we should not add the "Manage Trust" from the entity because
    we don't deal with the information trust of "other" category by design.
  */
  if (
    l0_labels_from_event.length > 1 ||
    (l0_labels_from_event.length == 1 &&
      l0_labels_from_event[0] != SharedState.default_label_id.value)
  ) {
    resolve_section_sub_items.push(
      ManageTrustMenuItem([...employees, ...organizations]),
    );
  }

  if (
    !p.hide_justification &&
    does_user_never_asked_for_justification &&
    SharedState.FeatureHelper.is_enabled("justification_automation")
  ) {
    resolve_section_sub_items.push(justification_menu_item);
  }

  if (resolve_section_sub_items.length > 0) {
    menu.push({
      name: "Resolve",
      icon: "Fix",
      isCategory: true,
      disable: false,
      isPremium: false,
      subItems: resolve_section_sub_items,
    });
  }

  const menuRes = await UseDrillDownMenu().show({
    menu,
    event: p.event,
  });

  if (menuRes.didUserSelectedAnAction) {
    const action = menuRes.actionSelected;
    const { is_restricted_app } = SharedState.restrictions;
    if (is_restricted_app("trust_management") && action.startsWith("manage_")) {
      SharedState.restrictions.show_restrictions_modal(
        "app",
        "Trust Management",
      );
      return "idle";
    }
    if (
      SharedState.restrictions.is_restricted_app("entity_investigation") &&
      action.startsWith("investigate")
    ) {
      SharedState.restrictions.show_restrictions_modal(
        "app",
        "Entity Investigation",
      );
      return "idle";
    }

    switch (true) {
      case action === justification_action_name:
        justification_handler(p.item);
        break;
      case action === "investigate_sender":
        SharedState.investigation.investigate({
          type: POPULATION_TYPE.EMPLOYEE,
          name: exposedBy.name,
          email: exposedBy.email,
          id: exposedBy.id,
        });
        return "close";
      case action.startsWith("investigate_exposedTo_"): {
        const personId = action.split("_").pop();
        const person = eventEntities.find((r: any) => r.id === personId);
        let type;
        switch (person.type) {
          case "organization":
            type = POPULATION_TYPE.ORGANIZATION;
            break;
          case "person":
          default:
            type = person.isExternal
              ? POPULATION_TYPE.CONTACT
              : POPULATION_TYPE.EMPLOYEE;
            break;
        }

        SharedState.investigation.investigate({
          type: type,
          name: person.name,
          email: person.email,
          id: person.id,
        });
        return "close";
      }
      case action.startsWith("manage_"):
        const [, entity_id, entity_type] = action.split("_");
        await UseModal().show(ManageTrustModalVue, {
          entity: {
            ...(entity_type == "organization"
              ? organizations.find((d) => d.id === entity_id)
              : employees.find((d) => d.id === entity_id)),
            type:
              entity_type === "organization"
                ? POPULATION_TYPE.ORGANIZATION
                : POPULATION_TYPE.EMPLOYEE,
          },
          info_types: uniq(l0_labels_from_event),
        });
        return "idle";
      case action.startsWith("investigate_file_"):
      default: {
        const fileId = action.split("_").pop();
        const file = eventData.files.find((f: any) => f.id === fileId);
        SharedState.investigation.investigate({
          type: POPULATION_TYPE.FILE,
          name: file.name,
          id: file.id,
          cluster_id: file.clusterUID,
        });

        return "close";
      }
    }
  }
  return "idle";
};
